/** #754/#902 shared overview selection. Pure, over the admitted browser
 * catalog; no requests, subscriptions, navigation or order/focus changes.
 */
import type { GameRow, MarketType, StatusFilter, Venue, VenueBook } from './types';
import { addDaysIso, isSlateDate } from './weeklySlate';

export type DiscoveryMode = 'manual' | 'today' | 'week';
export type WeekDefinition = 'calendar-monday' | 'rolling-seven';
export interface DiscoveryReading {
  mode: DiscoveryMode;
  query: string;
  marketType: MarketType;
  status: StatusFilter;
  venue: Venue | 'all';
  /** Saved manual scope is retained when search/discovery temporarily widens it. */
  manualSports: readonly string[] | null;
  manualRange: { startIso: string; endIso: string } | null;
}
export interface DiscoveryVolume {
  value: number | null;
  coverage: 'complete' | 'partial' | 'missing' | 'unsupported';
}
export interface DiscoveryGroup<T> {
  /** Exact rendered group identity, including game number/market family. */
  key: string;
  sport: string;
  marketType: MarketType;
  status: GameRow['gameState'] | null;
  venues: readonly Venue[];
  startTimeMs: number | null;
  date: string | null;
  searchText: string;
  volume: DiscoveryVolume;
  row: T;
}

function measured(book: VenueBook): number | null {
  return !book.volumeIsDepthDerived && Number.isFinite(book.volume) && book.volume >= 0 ? book.volume : null;
}

/** The existing moneyline blue-box sum: both independent Kalshi markets,
 * Polymarket's binary volume once. Unsupported families never borrow it.
 * Absent venues are not missing observations from a listed venue.
 */
export function moneylineDiscoveryVolume(game: GameRow, marketType: MarketType): DiscoveryVolume {
  if (marketType !== 'moneyline' || game.series || game.spreadOutcome || game.kalshiViaSeries || game.kalshiViaTournament) {
    return { value: null, coverage: 'unsupported' };
  }
  const books = [game.kalshi?.away, game.kalshi?.home, game.polymarket?.away]
    .filter((book): book is VenueBook => book != null);
  if (books.length === 0) return { value: null, coverage: 'missing' };
  const values = books.map(measured);
  const known = values.filter((v): v is number => v !== null);
  if (known.length === 0) return { value: null, coverage: 'missing' };
  const value = known.reduce((sum, v) => sum + v, 0);
  if (!Number.isFinite(value)) return { value: null, coverage: 'missing' };
  return { value, coverage: known.length === values.length ? 'complete' : 'partial' };
}

/** Only existing names/codes/tickers. No guessed alias or fuzzy-match database. */
export function gameRowDiscoveryGroup(game: GameRow, marketType: MarketType): DiscoveryGroup<GameRow> {
  const books = [game.kalshi?.away, game.kalshi?.home, game.polymarket?.away, game.polymarket?.home];
  return { key: game.key, sport: game.sport, marketType, status: game.gameState,
    venues: [...(game.kalshi ? ['kalshi' as const] : []), ...(game.polymarket ? ['polymarket' as const] : [])],
    startTimeMs: game.startTimeMs, date: isSlateDate(game.date) ? game.date : null,
    searchText: [game.sport, marketType, game.awayCode, game.homeCode, game.awayName,
      game.homeName, ...books.map(b => b?.marketTicker ?? '')].join(' ').normalize('NFKC').toLowerCase(),
    volume: moneylineDiscoveryVolume(game, marketType), row: game };
}

let cachedZone: string | null = null;
let cachedDateFormatter: Intl.DateTimeFormat | null = null;
export function calendarDateInZone(ms: number, timeZone: string): string {
  if (!Number.isFinite(ms)) throw new Error('A finite clock is required');
  if (cachedZone !== timeZone || !cachedDateFormatter) {
    cachedDateFormatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
    cachedZone = timeZone;
  }
  const parts = cachedDateFormatter.formatToParts(ms);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** Policy is explicit so product confirmation changes one argument, not a
 * football/accounting helper. The UI must display the resulting exact range.
 */
export function discoveryRange(mode: DiscoveryMode, nowMs: number, timeZone: string,
  week: WeekDefinition, manual: DiscoveryReading['manualRange']): DiscoveryReading['manualRange'] {
  if (mode === 'manual') return manual;
  const today = calendarDateInZone(nowMs, timeZone);
  if (mode === 'today') return { startIso: today, endIso: today };
  const weekday = new Date(`${today}T00:00:00Z`).getUTCDay();
  const startIso = week === 'rolling-seven' ? today : addDaysIso(today, -((weekday + 6) % 7));
  return { startIso, endIso: addDaysIso(startIso, 6) };
}

export function commitDiscoveryQuery(reading: DiscoveryReading, query: string): DiscoveryReading {
  return { ...reading, query: query.trim().replace(/\s+/g, ' ') };
}

/** Clear/Escape restores the retained manual or discovery scope; no navigation. */
export function clearDiscoveryQuery(reading: DiscoveryReading): DiscoveryReading {
  return { ...reading, query: '' };
}

export function selectDiscoveryGroups<T>(groups: readonly DiscoveryGroup<T>[], reading: DiscoveryReading,
  options: { nowMs: number; timeZone: string; week: WeekDefinition; visibleCount?: number }) {
  const query = reading.query.trim().normalize('NFKC').toLowerCase();
  const terms = query.split(/\s+/).filter(Boolean);
  const allSports = reading.mode !== 'manual' || terms.length > 0;
  const range = discoveryRange(reading.mode, options.nowMs, options.timeZone, options.week, reading.manualRange);
  if (range && (!isSlateDate(range.startIso) || !isSlateDate(range.endIso) || range.startIso > range.endIso)) {
    throw new Error('Invalid discovery date range');
  }
  // Ambiguous duplicate identities are excluded, not resolved by array order.
  const counts = new Map<string, number>();
  for (const group of groups) counts.set(group.key, (counts.get(group.key) ?? 0) + 1);
  const eligible = groups.filter(group => {
    if (!group.key || counts.get(group.key) !== 1 || group.marketType !== reading.marketType) return false;
    if (!allSports && reading.manualSports && !reading.manualSports.includes(group.sport)) return false;
    if (reading.venue !== 'all' && !group.venues.includes(reading.venue)) return false;
    if (reading.status === 'active' && (group.status === null || group.status === 'final')) return false;
    if (reading.status === 'settled' && group.status !== 'final') return false;
    if (!terms.every(term => group.searchText.normalize('NFKC').toLowerCase().includes(term))) return false;
    if (range) {
      // Reader-local date when start is known; a canonical calendar date is
      // retained as such when no timestamp exists, never shifted by UTC parsing.
      const date = group.startTimeMs !== null && Number.isFinite(group.startTimeMs)
        ? calendarDateInZone(group.startTimeMs, options.timeZone) : group.date;
      if (!date || !isSlateDate(date) || date < range.startIso || date > range.endIso) return false;
    }
    return true;
  });
  if (reading.mode !== 'manual') eligible.sort((a, b) => {
    const av = a.volume.value, bv = b.volume.value;
    // Unknown is below measured zero. A partial known sum stays explicitly partial.
    if (av === null && bv !== null) return 1;
    if (av !== null && bv === null) return -1;
    return (bv ?? 0) - (av ?? 0) || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
  });
  const limit = options.visibleCount ?? 20;
  if (!Number.isSafeInteger(limit) || limit < 1) throw new Error('Positive visible count required');
  return { groups: eligible, visible: eligible.slice(0, limit), total: eligible.length,
    remaining: Math.max(0, eligible.length - limit), range, timeZone: options.timeZone, allSports,
    excludedDuplicateKeys: [...counts].filter(([, count]) => count > 1).map(([key]) => key) };
}

/** A pending pointer/keyboard selection captures identity, never an array index.
 * Re-resolve against current eligible membership when navigation is committed.
 */
export function resolveDiscoverySelection<T>(key: string, current: readonly DiscoveryGroup<T>[]): T | null {
  const matches = current.filter(group => group.key === key);
  return matches.length === 1 ? matches[0].row : null;
}
